import { action } from 'mobx';
import { SessionStore } from './session.store';
import { AggregateFunction, AgtAnalyticsStore, SelectColumn } from './agt.analytics.store';

export interface IAgtInstagramModel {
	vtnrId: number;
	bm: number;
	instagramUrl: string;
	seitenStatus: number;
	follower: number;
	views: number;
	reichweite: number;
	postinganzahl: number;
	aboPr: number;
	vtEigenePosts: number;
}

export class AgtInstagramModel implements IAgtInstagramModel {
	vtnrId: number;
	bm: number;
	instagramUrl: string;
	seitenStatus: number;
	follower: number;
	views: number;
	reichweite: number;
	postinganzahl: number;
	aboPr: number;
	vtEigenePosts: number;

	// Legacy compatibility properties
	agtId: number;
	j: any;

	constructor(data: any) {
		this.vtnrId = data.aid || 0;
		this.bm = data.bm ? parseInt(data.bm) : 0;
		this.instagramUrl = data.u || '';
		this.seitenStatus = data.s || 0;
		this.follower = data.f || 0;
		this.views = data.v || 0;
		this.reichweite = data.r || 0;
		this.postinganzahl = data.p || 0;
		this.aboPr = data.a || 0;
		this.vtEigenePosts = data.w || 0;

		// Legacy compatibility - alias properties
		this.agtId = this.vtnrId;

		// Legacy j property for backwards compatibility
		this.j = {
			u: this.instagramUrl,
			s: this.seitenStatus,
			f: this.follower,
			v: this.views,
			r: this.reichweite,
			p: this.postinganzahl,
			a: this.aboPr,
			w: this.vtEigenePosts
		};
	}
}

export class AgtInstagramStore extends AgtAnalyticsStore {
	constructor(session: SessionStore) {
		super(session, {
			tableName: 'agentur_instagram',
			defaultColumns: [
				{ column: 'vtnr_id', aggregateFunction: AggregateFunction.NONE, alias: 'aid' },
				{ column: 'bm', aggregateFunction: AggregateFunction.NONE },
				{ column: 'instagram_url', aggregateFunction: AggregateFunction.NONE, alias: 'u' },
				{ column: 'seiten_status', aggregateFunction: AggregateFunction.NONE, alias: 's' },
				{ column: 'follower', aggregateFunction: AggregateFunction.NONE, alias: 'f' },
				{ column: 'views', aggregateFunction: AggregateFunction.NONE, alias: 'v' },
				{ column: 'reichweite', aggregateFunction: AggregateFunction.NONE, alias: 'r' },
				{ column: 'postinganzahl', aggregateFunction: AggregateFunction.NONE, alias: 'p' },
				{ column: 'abo_pr', aggregateFunction: AggregateFunction.NONE, alias: 'a' },
				{ column: 'vt_eigene_posts', aggregateFunction: AggregateFunction.NONE, alias: 'w' },
			],
		});
		this.queryName = '?instagram';
	}

	async getDistinctBMs(): Promise<number[]> {
		const selectColumns: SelectColumn[] = [
			{ column: 'bm', aggregateFunction: AggregateFunction.NONE, distinct: true },
		];
		const results = await this.query(selectColumns);
		return results.map(data => parseInt(data.bm)).sort((a, b) => a - b);
	}

	@action
	async loadAgtByBm(agtId: number, bm: number): Promise<AgtInstagramModel[]> {
		let whereClause = 'a.vtnr_id = ' + agtId + ' AND bm = ' + bm;
		const results = await this.query(this.config.defaultColumns, whereClause);
		return results.map(data => new AgtInstagramModel(data));
	}

	@action
	async aggreagteYtd(bm: number): Promise<any[]> {
		if (!bm || bm <= 0) {
			return [];
		}
		let firstBmOfYear = Math.floor(bm / 100) * 100 + 1;
		let whereClause = `bm >= ${firstBmOfYear} AND bm <= ${bm}`;
		const selectColumns: SelectColumn[] = [
			{ column: 'vtnr_id', aggregateFunction: AggregateFunction.NONE, alias: 'aid', groupBy: true },
			{ column: 'postinganzahl', aggregateFunction: AggregateFunction.SUM, alias: 'p', },
			{ column: 'vt_eigene_posts', aggregateFunction: AggregateFunction.SUM, alias: 'w', },
			{ column: 'views', aggregateFunction: AggregateFunction.SUM, alias: 'v' },
		];

		return await this.query(selectColumns, whereClause);

	}

	@action
	async loadByBmRange(minBm: number, maxBm: number): Promise<AgtInstagramModel[]> {
		if (!minBm || !maxBm || minBm <= 0 || maxBm <= 0) {
			return [];
		}
		// raw monthly rows, no ytd merge -> sums are computed client-side over the range
		const whereClause = ` bm >= ${minBm} AND bm <= ${maxBm} `;
		const results = await this.query(this.config.defaultColumns, whereClause);
		return results.map((data) => new AgtInstagramModel(data));
	}

	@action
	async loadByBm(bm: number): Promise<AgtInstagramModel[]> {
		if (!bm || bm <= 0) {
			return [];
		}
		let whereClause = ` bm = ${bm} `;
		const [results, aggForBM] = await Promise.all([
			this.query(this.config.defaultColumns, whereClause),
			this.aggreagteYtd(bm)
		]);

		// Merge aggregated YTD data into results where aid matches and bm equals bm
		return results.map(data => {
			if (data.bm === bm) {
				const agg = aggForBM.find(a => a.aid === data.aid);
				if (agg) {
					// Merge aggregated values into the result
					return new AgtInstagramModel({
						...data,
						p: agg.p, // postinganzahl ytd
						w: agg.w, // vt_eigene_posts ytd
						v: agg.v // views ytd
					});
				}
			}
			return new AgtInstagramModel(data);
		});
	}
}
